// src/services/notificationsInboxService.js
import supabase from '../lib/supabase';

/* ============================================================
   Notifications inbox service — fetch only, no realtime
   ============================================================ */

export async function fetchNotifications(userId, {
    limit = 20,
    page = 0,
    channel = 'all',
    status = 'all',
} = {}) {
    if (!userId) throw new Error('fetchNotifications: missing userId');

    const from = page * limit;
    const to = from + limit - 1;

    let q = supabase
        .from('notifications')
        .select('id, channel, title, body, link, read_at, metadata, created_at', { count: 'exact' })
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(from, to);

    if (channel && channel !== 'all') q = q.eq('channel', channel);
    if (status === 'unread') q = q.is('read_at', null);
    if (status === 'read') q = q.not('read_at', 'is', null);

    const { data, error, count } = await q;
    if (error) throw error;

    return { rows: data || [], total: count || 0 };
}

export async function fetchUnreadCount(userId) {
    if (!userId) return 0;
    const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .is('read_at', null);
    if (error) throw error;
    return count || 0;
}

export async function markAsRead(userId, notificationId) {
    const { data, error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('user_id', userId)
        .is('read_at', null)
        .select()
        .maybeSingle();
    if (error) throw error;
    return data;
}

export async function markAllAsRead(userId) {
    const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('user_id', userId)
        .is('read_at', null);
    if (error) throw error;
}

export async function deleteNotification(userId, notificationId) {
    const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)
        .eq('user_id', userId);
    if (error) throw error;
}

export async function deleteAllRead(userId) {
    const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', userId)
        .not('read_at', 'is', null);
    if (error) throw error;
}

/* ---------------- CSV export ---------------- */
export function downloadNotificationsCsv(rows) {
    const headers = ['Received', 'Channel', 'Title', 'Body', 'Link', 'Read'];
    const lines = [headers.join(',')];

    rows.forEach((n) => {
        const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        lines.push([
            esc(n.created_at ? new Date(n.created_at).toISOString() : ''),
            esc(n.channel),
            esc(n.title),
            esc(n.body),
            esc(n.link),
            esc(n.read_at ? 'yes' : 'no'),
        ].join(','));
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sohocard-notifications-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}